// src/components/proposals/SilverTemplate.jsx
//
// Silver Guardian proposal template — React port of
// Proposal-Template-Silver.dc.html (Claude Designs standalone).
//
// STRUCTURAL PORT, NOT A REDESIGN. Ported page-by-page against the raw source,
// not from a summary of it. Where the written spec and the HTML disagreed, the
// HTML won and the difference is called out in a comment at the site.
//
// SILVER IS A SLATE DOCUMENT, NOT A TEAL ONE. Its accent appears 113 times
// across the 10 pages — every eyebrow, dash, icon plate, band, tick and stat —
// against 16 teal, and the teal that remains is almost entirely the Gold
// Fortress upsell card, which is selling Gold and therefore stays Gold's
// colour. Colours here are named constants rather than inline hex precisely so
// that identity is one edit, not 113.
//
// Pricing is IMPORTED from BronzeTemplate, not re-transcribed. Gold keeps a
// local copy and that duplication is a known liability; adding a third would
// mean a rate change could land in two of three documents and be invisible in
// the one that missed it.
import React from "react";
import {
  computeBronzeTotals, MAX_LIST_ITEMS,
  CHALLENGE_ICONS, FINDING_ICONS,
  M365_PLANS, networkCoveredH, m365ItemRateSuspect,
} from "./BronzeTemplate";
import {
  PAGE_BASE, PAGE_BODY_H, CoverPage, HeaderBar, GhostNumber, PageFooter,
  SectionLabel, SectionBand, SplitRow, TwoColTable, BulletRow, dot, F, Icon,
  EmptyBlock, NumberCell, CheckBox, AddButton, DeleteButton, MoneyInput,
} from "./proposalChrome";
import { packSegments } from "./pagePacking";
import { computeBlockSubtotal, lineItemTotal, fmtMoney,
         chunkFoundationalBlocks,
         DEFAULT_FOUNDATIONAL_PRICING_NOTE } from "./foundationalBlocks";

/* ══════════════════════════════════════════════════════════════════════
   Silver's colour identity
   ══════════════════════════════════════════════════════════════════════ */
export const SILVER = {
  /* Solid fills, left-accent bars, badges, icon plates. */
  accent:     "#64748b",
  /* Eyebrows, wordmark, ticks, stat figures — the lighter tone that reads on
     the dark header bar and inside the dark cards. */
  accentSoft: "#94a3b8",
  /* The comparison table's own column: a darker tick on a tinted cell, so
     Silver's column reads as the recommended one without a second badge. */
  tick:       "#475569",
  columnTint: "#f1f5f9",
  /* Three stops, not two — slate resolving into the brand teal. Bronze and
     Gold's cover bars are two-stop. */
  topBar:     "linear-gradient(90deg, #64748b, #94a3b8, #0b80a5)",
};

/* Brand teal. On this document it appears ONLY where something is selling Gold
   Fortress — the upsell card, and the once-off total figure that the fee-waiver
   badges point at. Everywhere else is slate. */
const TEAL = "#0b80a5";

export const SILVER_PLAN = "Silver Guardian";

/* ══════════════════════════════════════════════════════════════════════
   Defaults
   ══════════════════════════════════════════════════════════════════════ */
export function DEFAULT_SILVER_CONTENT() {
  return {
    customerName:      "Customer Company Name",
    customerFullName:  "Contact Full Name",
    customerContact:   "First Name",
    // Not rendered anywhere in the document. DocuSign cannot address an
    // envelope without it, and the content model captured no email at all.
    customerEmail:     "",
    proposalDate:      "Month Year",
    salesRepName:      "Your Name",
    salesRepTitle:     "Your Title",

    recommendedPlan:   SILVER_PLAN,

    deviceCount:       5,
    userCount:         5,
    perDeviceRate:     49,
    perUserRate:       95,
    onboardingFee:     625,
    intunePerDevRate:  110,
    intuneSetupFee:    599,

    /* Explicitly false, even though the brief said to omit the field entirely.
       Omitting it would leave Silver inheriting whatever DEFAULT_BRONZE_CONTENT
       happens to hold — false today, but a change there would silently start
       waiving Silver's fees. Silver never waives: the amber badge on those two
       rows is upsell copy pointing at Gold, not a concession Silver is making,
       which is exactly Bronze's behaviour and why the badge is static. */
    waiveSetupFees:    false,

    /* Copper Defender and the cross-tier Bronze grouping. Both are supplementary
       DEVICE groupings billed on the same contract as the tier itself.
       copperRate carries its real amount rather than 0 for the same reason the
       setup fees do: a zeroed rate throws the number away, and turning the
       toggle on later would then quote $0/device. */
    addOnCopper:        false,
    copperDeviceCount:  0,
    copperRate:         15,
    addOnBronze:        false,
    bronzeDeviceCount:  0,
    addOnNetwork:       false,
    networkDeviceCount: 5,
    networkMonitorDevicesCovered: "",
    addOnBackup:        false,
    addOnSpamThreat:    false,
    addOnEmailSignature: false,
    showM365Standard:   true,
    m365PremiumQty:     null,
    m365PlanChoice:     "premium",
    additionalM365Items: [],

    /* 3CX Phone System — off by default. `threeCXHostingFee` starts EMPTY
       rather than 0: these are free-text money fields and a literal zero is
       what made typing "10" read "010". The SIP trunk price seeds a real $90
       because that is a meaningful starting rate, not an "unset" placeholder —
       an ASSUMED default from Craig's example, editable per proposal. */
    include3CX:                 false,
    threeCXHostingFee:          "",
    threeCXSipTrunkDescription: "",
    threeCXSipTrunkPrice:       90,
    additionalRows:     [],

    introPara1: "", introPara2: "", introPara3: "",
    challenges: [], findings: [],
    // `projects` is deliberately absent. The brief carried it as a "legacy
    // field", but it was superseded by foundationalBlocks and nothing reads it;
    // seeding dead data would only invite someone to wire it back up.
    foundationalBlocks: [],
    // Estimate-only caveat printed under the Foundational intro. Seeded from
    // the shared constant so the three tiers cannot state it differently.
    foundationalPricingNote: DEFAULT_FOUNDATIONAL_PRICING_NOTE,

    showIntro:         true,
    showWhatHeard:     true,
    showWhatFound:     true,
    showRecommend:     true,
    showComparison:    true,
    showInvestment:    true,
    showResponseTerms: true,
    showFoundational:  true,
    showWhyUs:         true,

    coverImageId: null,
  };
}

const fmt    = (n) => "$" + Number(n).toLocaleString("en-AU");
const fmtMth = (n) => "$" + Number(n).toLocaleString("en-AU") + "/mth";
// One-off amounts are shown to the cent. toLocaleString, not toFixed:
// toFixed drops the thousands separator ("$2104.00").
const fmtC   = (n) => "$" + Number(n || 0).toLocaleString("en-AU",
                        { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmt2   = fmtC;

/* ══════════════════════════════════════════════════════════════════════
   PAGE 2 — Introduction
   ══════════════════════════════════════════════════════════════════════ */
const INTRO_PLACEHOLDERS = [
  "✏️ Paragraph 1 — Open with a personal connection. Reference where you met, thank them for their time, and acknowledge something specific about their business.",
  "✏️ Paragraph 2 — Bridge to the proposal. Explain that everything they shared, combined with your on-site observations, has shaped the recommendations inside.",
  "✏️ Paragraph 3 — Close with confidence. Reinforce your commitment to their success and your excitement about partnering with them.",
];

function IntroPage({ c, mode, set }) {
  const paraStyle = { fontSize: 14.5, lineHeight: 1.75, color: "#1f2937", margin: 0 };
  const keys = ["introPara1", "introPara2", "introPara3"];
  return (
    <div className="page" style={PAGE_BASE}>
      <HeaderBar customerName={c.customerName} accent={SILVER.accentSoft} />
      <GhostNumber n="01" />
      <div style={{ padding: "44px 80px 44px", position: "relative", minHeight: PAGE_BODY_H }}>
        <SectionLabel text="Section 01 — Introduction" accent={SILVER.accentSoft} />
        <p style={{ fontSize: 24, fontWeight: 800, color: "#1a1a1a", margin: "0 0 36px",
                    letterSpacing: "-0.02em" }}>
          Hi{" "}
          <F mode={mode} value={c.customerContact} onChange={(v) => set({ customerContact: v })}
             inline placeholder="first name"
             style={{ fontSize: 24, fontWeight: 800, color: "#1a1a1a", letterSpacing: "-0.02em" }} />
          ,
        </p>

        <div>
          {keys.map((k, i) => {
            const val = c[k];
            if (mode !== "edit" && !val) return null;   // never ship the coaching text
            return (
              <div key={k} style={{ marginBottom: 18 }}>
                <F mode={mode} value={val} onChange={(v) => set({ [k]: v })}
                   placeholder={INTRO_PLACEHOLDERS[i]} multiline rows={4} style={paraStyle} />
              </div>
            );
          })}
        </div>

        <div style={{ marginTop: 44, paddingTop: 28, borderTop: "1px solid #f0f0f0" }}>
          <p style={{ fontSize: 13.5, color: "#4b5563", margin: "0 0 14px" }}>Best regards,</p>
          <p style={{ fontSize: 20, fontWeight: 800, color: "#1a1a1a", margin: "0 0 4px",
                      letterSpacing: "-0.02em" }}>{c.salesRepName}</p>
          <p style={{ fontSize: 13, color: "#4b5563", margin: 0, fontWeight: 500 }}>{c.salesRepTitle}</p>
          <p style={{ fontSize: 13, color: SILVER.accent, margin: "3px 0 0", fontWeight: 600 }}>
            Datatech Solutions
          </p>
        </div>
      </div>
      <PageFooter />
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════════════
   PAGES 3 & 4 — What We Heard / What We've Found
   ══════════════════════════════════════════════════════════════════════ */
function ItemCard({ item, index, mode, icons, accent, iconBg, iconStroke, onPatch, onDelete }) {
  return (
    <div style={{ background: "#f8fafc", border: "1px solid #e5e9ef",
                  borderLeft: `4px solid ${accent}`, borderRadius: 8, padding: 16,
                  position: "relative" }}>
      {mode === "edit" && (
        <div style={{ position: "absolute", top: 8, right: 8 }}>
          <DeleteButton onClick={onDelete} />
        </div>
      )}
      <div style={{ width: 28, height: 28, background: iconBg, borderRadius: 6,
                    marginBottom: 9, textAlign: "center", lineHeight: "28px" }}>
        <Icon paths={icons[index % icons.length]} stroke={iconStroke} />
      </div>
      <h3 style={{ fontSize: 13.5, fontWeight: 700, color: "#1a1a1a", margin: "0 0 6px",
                   letterSpacing: "-0.01em" }}>
        <F mode={mode} value={item.title} onChange={(v) => onPatch({ title: v })}
           placeholder="Title…"
           style={{ fontSize: 13.5, fontWeight: 700, color: "#1a1a1a", letterSpacing: "-0.01em" }} />
      </h3>
      <p style={{ fontSize: 12, color: "#4b5563", lineHeight: 1.6, margin: 0 }}>
        <F mode={mode} value={item.description} onChange={(v) => onPatch({ description: v })}
           placeholder="Describe…" multiline rows={3}
           style={{ fontSize: 12, color: "#4b5563", lineHeight: 1.6 }} />
      </p>
    </div>
  );
}

function ListCardPage({ c, mode, set, cfg }) {
  const items = Array.isArray(c[cfg.key]) ? c[cfg.key] : [];
  const patch = (i, p) => set({ [cfg.key]: items.map((r, j) => (j === i ? { ...r, ...p } : r)) });
  const remove = (i) => set({ [cfg.key]: items.filter((_, j) => j !== i) });
  const add = () => {
    if (items.length >= MAX_LIST_ITEMS) return;
    set({ [cfg.key]: [...items, { title: "", description: "" }] });
  };

  return (
    <div className="page" style={PAGE_BASE}>
      <HeaderBar customerName={c.customerName} accent={SILVER.accentSoft} />
      <GhostNumber n={cfg.ghost} />
      <div style={{ padding: "44px 64px 52px", position: "relative", minHeight: PAGE_BODY_H }}>
        <SectionLabel text={cfg.section} accent={SILVER.accentSoft} />
        <h2 style={{ fontSize: 28, fontWeight: 800, color: "#1a1a1a", margin: "0 0 6px",
                     letterSpacing: "-0.02em" }}>{cfg.heading}</h2>
        <p style={{ fontSize: 13.5, color: "#4b5563", margin: "0 0 16px", lineHeight: 1.5 }}>
          {cfg.subtitle(c.customerName)}
        </p>

        {items.length === 0 ? (
          <EmptyBlock title={cfg.emptyTitle} hint={cfg.emptyHint} />
        ) : (
          <TwoColTable
            items={items}
            renderItem={(item, i) => (
              <ItemCard item={item} index={i} mode={mode} icons={cfg.icons}
                        accent={cfg.accent} iconBg={cfg.iconBg} iconStroke={cfg.iconStroke}
                        onPatch={(p) => patch(i, p)} onDelete={() => remove(i)} />
            )}
          />
        )}

        {mode === "edit" && (
          <AddButton label={`${cfg.addLabel} (${items.length}/${MAX_LIST_ITEMS})`}
                     accent={SILVER.accent}
                     onClick={add} disabled={items.length >= MAX_LIST_ITEMS} />
        )}
      </div>
      <PageFooter />
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════════════
   PAGE 5 — Our Recommendation

   Silver sits in the MIDDLE of the range, which is what makes this page
   different from both siblings: unlike Bronze it has helpdesk and SOC
   monitoring to show off, and unlike Gold it has something above it to sell.
   ══════════════════════════════════════════════════════════════════════ */

/* Read straight off the source's plan card, in its order.
   ⚠️ The written spec listed 12 of these (8 included, 4 excluded). The HTML has
   14: it also includes "Managed Device Support" and excludes "Microsoft 365
   Maintenance". Dropping either would understate what Silver buys or overstate
   what it covers, so the HTML's list is the one used. */
const SILVER_INCLUDED = [
  "Managed Device Support",
  "Remote Monitoring & Management",
  "Monthly Patch Management",
  "Device Health Monitoring",
  "Proactive Device Maintenance",
  "Ransomware Detection & Response",
  "Endpoint Threat Protection",
  "24/7 SOC Threat Monitoring",
  "Helpdesk Support (Unlimited Remote)",
];
const SILVER_EXCLUDED = [
  "Dark Web Monitoring",
  "M365 Intune & Defender Management",
  "Dedicated Account Manager",
  "Microsoft 365 Maintenance",
  "Phishing Campaign & Training",
];

/* The Gold upsell, with the one-line reason each item matters.
   ⚠️ The spec listed six, opening with "24/7 SOC Threat Monitoring" — but
   Silver ALREADY includes that (see SILVER_INCLUDED above), so advertising it
   as something Gold would add tells the customer they are buying something they
   already have. The source omits it and so does this. */
/* Gold's per-user rate. Declared BEFORE the component that reads it, not after:
   a const referenced ahead of its declaration survives development and then
   fails only in the minified production bundle as "Cannot access before
   initialization" — a pattern this codebase has already been bitten by. */
const GOLD_PER_USER_RATE = 120;

const GOLD_UPSELL = [
  ["Dedicated Account Manager", "Strategic guidance and proactive planning, not just reactive tickets."],
  ["Dark Web Monitoring", "Stolen credentials trade online long before they're ever used against you."],
  ["Phishing Campaigns & Training", "Staff remain the most common way attackers get in the door."],
  ["M365 Intune & Defender Mgmt", "Unmanaged identity and device policy is an open door for attackers."],
  ["Microsoft 365 Maintenance", "Your tenant stays configured, licensed and secure as Microsoft's platform evolves."],
];

function RecommendPage({ c, t }) {
  const features = [
    ...SILVER_INCLUDED.map((label) => ({ label, on: true })),
    ...SILVER_EXCLUDED.map((label) => ({ label, on: false })),
  ];

  return (
    <div className="page" style={PAGE_BASE}>
      <HeaderBar customerName={c.customerName} accent={SILVER.accentSoft} />
      <GhostNumber n="04" />
      <div style={{ padding: "44px 64px 52px", position: "relative", minHeight: PAGE_BODY_H }}>
        <SectionLabel text="Section 04" accent={SILVER.accentSoft} />
        <h2 style={{ fontSize: 28, fontWeight: 800, color: "#1a1a1a", margin: "0 0 6px",
                     letterSpacing: "-0.02em" }}>Our Recommendation</h2>
        <p style={{ fontSize: 13.5, color: "#4b5563", margin: "0 0 20px", lineHeight: 1.5 }}>
          Based on our discovery and assessment, we recommend the following managed services
          plan for {c.customerName}.
        </p>

        {/* Featured plan card */}
        <div style={{ background: "#1a1a1a", borderRadius: 10, padding: "22px 34px",
                      position: "relative", overflow: "hidden", marginBottom: 14 }}>
          <div style={{ position: "absolute", top: 0, left: 0, right: 0, height: 4,
                        background: SILVER.topBar }} />
          <div style={{ position: "absolute", top: -60, right: -60, width: 220, height: 220,
                        borderRadius: "50%", background: "rgba(100,116,139,0.09)",
                        pointerEvents: "none" }} />

          <SplitRow
            align="top"
            style={{ marginBottom: 18, position: "relative" }}
            left={
              <>
                <p style={{ color: SILVER.accentSoft, fontSize: 10, fontWeight: 700,
                            letterSpacing: "0.18em", textTransform: "uppercase",
                            margin: "0 0 8px" }}>Our Recommended Plan</p>
                <h3 style={{ color: "#ffffff", fontSize: 30, fontWeight: 800, margin: 0,
                             letterSpacing: "-0.02em" }}>{c.recommendedPlan}</h3>
              </>
            }
            right={
              <>
                <p style={{ color: "rgba(255,255,255,0.4)", fontSize: 10, textTransform: "uppercase",
                            letterSpacing: "0.1em", margin: "0 0 5px" }}>Per Device</p>
                <p style={{ color: SILVER.accentSoft, fontSize: 22, fontWeight: 800, margin: 0,
                            letterSpacing: "-0.02em" }}>
                  {fmt(t.perDevRate)}
                  <span style={{ fontSize: 13, fontWeight: 500, color: "rgba(255,255,255,0.5)" }}>/mth</span>
                </p>
                {/* Silver charges per user as well as per device. Bronze's card
                    says "no per-user fee" in this slot — printing that here
                    would be a commercial misstatement, the same trap Gold hit. */}
                <p style={{ color: "#ffffff", fontSize: 13, fontWeight: 700, margin: "4px 0 0" }}>
                  + {fmt(t.perUsrRate)}
                  <span style={{ fontSize: 11, fontWeight: 500, color: "rgba(255,255,255,0.5)" }}>/user/mth</span>
                </p>
              </>
            }
          />

          <div style={{ height: 1, background: "rgba(255,255,255,0.1)", marginBottom: 16 }} />

          <TwoColTable
            items={features}
            colGap={28}
            rowGap={7}
            style={{ position: "relative" }}
            renderItem={(f) => (
              <BulletRow
                gap={10}
                bullet={
                  <span style={{ color: f.on ? SILVER.accentSoft : "rgba(255,255,255,0.25)",
                                 fontWeight: 800, fontSize: 13, display: "inline-block",
                                 marginTop: 1 }}>
                    {f.on ? "✓" : "—"}
                  </span>
                }
              >
                <span style={{ color: f.on ? "rgba(255,255,255,0.82)" : "rgba(255,255,255,0.3)",
                               fontSize: 13, lineHeight: 1.4,
                               textDecoration: f.on ? "none" : "line-through",
                               textDecorationColor: "rgba(255,255,255,0.15)" }}>
                  {f.label}
                </span>
              </BulletRow>
            )}
          />
        </div>

        {/* Why we recommend this */}
        <div style={{ background: "#f8fafc", border: "1px solid #e2e8f0",
                      borderLeft: `4px solid ${SILVER.accent}`, borderRadius: 8,
                      padding: "16px 22px", marginBottom: 14 }}>
          <BulletRow
            gap={14}
            bulletWidth={30}
            bullet={
              <div style={{ width: 30, height: 30, background: SILVER.accent, borderRadius: 6,
                            marginTop: 1, textAlign: "center", lineHeight: "30px" }}>
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#fff"
                     strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="20 6 9 17 4 12" />
                </svg>
              </div>
            }
          >
            <p style={{ fontSize: 13, fontWeight: 700, color: "#1a1a1a", margin: "0 0 4px" }}>
              Why we recommend this for {c.customerName}
            </p>
            <p style={{ fontSize: 12.5, color: "#1f2937", lineHeight: 1.6, margin: 0 }}>
              Silver Guardian moves {c.customerName} beyond reactive break-fix IT — unlimited
              helpdesk support, proactive device maintenance, and ransomware protection from
              engineers who know your environment.
            </p>
          </BulletRow>
        </div>

        {/* Gold Fortress upsell — the one block on this page that stays TEAL.
            It is selling Gold, and dressing it in Silver's slate would make the
            upgrade look like more of the same. */}
        <div style={{ background: "#1a1a1a", borderRadius: 10, padding: "18px 26px",
                      position: "relative", overflow: "hidden" }}>
          <div style={{ position: "absolute", top: 0, left: 0, right: 0, height: 3,
                        background: "linear-gradient(90deg, #0b80a5, #2a4365)" }} />
          <div style={{ position: "absolute", top: -50, right: -50, width: 180, height: 180,
                        borderRadius: "50%", background: "rgba(11,128,165,0.08)",
                        pointerEvents: "none" }} />

          <SplitRow
            align="top"
            style={{ marginBottom: 12, position: "relative" }}
            left={
              <>
                <table style={{ borderCollapse: "collapse", marginBottom: 6 }}>
                  <tbody>
                    <tr>
                      <td style={{ verticalAlign: "middle", padding: 0, paddingRight: 9 }}>
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={TEAL}
                             strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
                        </svg>
                      </td>
                      <td style={{ verticalAlign: "middle", padding: 0 }}>
                        <span style={{ fontSize: 10, fontWeight: 700, color: TEAL,
                                       background: "rgba(11,128,165,0.15)", padding: "3px 9px",
                                       borderRadius: 20, letterSpacing: "0.08em",
                                       textTransform: "uppercase" }}>
                          Recommended Upgrade
                        </span>
                      </td>
                    </tr>
                  </tbody>
                </table>
                <p style={{ fontSize: 19, fontWeight: 800, color: "#ffffff", margin: 0,
                            letterSpacing: "-0.02em" }}>Go all-in with Gold Fortress</p>
              </>
            }
            right={
              <>
                <p style={{ color: "rgba(255,255,255,0.4)", fontSize: 10, textTransform: "uppercase",
                            letterSpacing: "0.1em", margin: "0 0 3px" }}>Add just</p>
                {/* DERIVED, not the literal "+$25" in the source. The increment
                    is Gold's per-user rate minus Silver's; hardcoding it would
                    quietly become wrong the first time either rate is edited,
                    and the error would read as a discount we then honoured. */}
                <p style={{ color: TEAL, fontSize: 26, fontWeight: 800, margin: 0,
                            letterSpacing: "-0.02em", lineHeight: 1 }}>
                  +{fmt(GOLD_PER_USER_RATE - t.perUsrRate)}
                  <span style={{ fontSize: 13, fontWeight: 600,
                                 color: "rgba(255,255,255,0.5)" }}>/user/mth</span>
                </p>
                <p style={{ color: "rgba(255,255,255,0.35)", fontSize: 10.5, margin: "4px 0 0" }}>
                  on top of your {c.recommendedPlan} rate
                </p>
              </>
            }
          />

          <p style={{ fontSize: 12.5, color: "rgba(255,255,255,0.65)", lineHeight: 1.5,
                      margin: "0 0 13px" }}>
            For a small step up, {c.customerName} gets complete enterprise-grade protection and a
            strategic technology partner in your corner — not just a helpdesk.
          </p>

          <TwoColTable
            items={GOLD_UPSELL}
            colGap={20}
            rowGap={9}
            renderItem={([title, body]) => (
              <>
                <BulletRow
                  gap={8}
                  bullet={<span style={{ color: TEAL, fontWeight: 800, fontSize: 13,
                                         display: "inline-block", marginTop: 1 }}>✓</span>}
                >
                  <span style={{ color: "#ffffff", fontSize: 12.5, fontWeight: 700,
                                 lineHeight: 1.4 }}>{title}</span>
                </BulletRow>
                <p style={{ fontSize: 11, color: "rgba(255,255,255,0.55)", lineHeight: 1.45,
                            margin: "2px 0 0 20px" }}>{body}</p>
              </>
            )}
          />
        </div>
      </div>
      <PageFooter />
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════════════
   PAGE 6 — Service Tier Comparison
   ══════════════════════════════════════════════════════════════════════ */
/* 14 rows. "Dark Web Monitoring" was added to Bronze and Gold at the same time
   as this shipped, on Craig's call, so all three tier documents show the same
   table — a prospect who receives two of them should not find them disagreeing
   about how many services exist. BronzeTemplate.FEATURE_MATRIX is the twin;
   a test pins the two lists identical. */
/* Columns are [Copper, Bronze, Silver, Gold] — the same left-to-right order as
   TIERS below, so the ticks visibly accumulate across the row (2 / 8 / 9 / 14).

   ⚠ COPPER IS ENDPOINT SECURITY ONLY: Endpoint Threat Protection and Monthly
   Patch Management, nothing else. It carried Managed Device Support and Remote
   Monitoring & Management until 2026-08-29, which advertised the managed-support
   tiers' headline inclusions on a $15/device product that does not deliver them
   — and simultaneously withheld patch management, which it does. Corrected on
   Craig's call. Do not "restore" either from an older source document. */
export const FEATURE_MATRIX = [
  /* ⚠ ORDER IS DELIBERATE, and it is the SECOND half of the Copper fix. With
     the columns running Copper→Gold, leading with Copper's own two features
     makes the table a staircase in both directions: more tiers carry each
     feature as you read RIGHT, and each tier's additions follow the previous
     tier's as you read DOWN. Sorting these alphabetically, or restoring the old
     order, breaks the read even though every tick stays correct. */
  ["Endpoint Threat Protection",      true,  true,  true,  true],
  ["Monthly Patch Management",        true,  true,  true,  true],
  ["Managed Device Support",          false, true,  true,  true],
  ["Remote Monitoring & Management",  false, true,  true,  true],
  ["Device Health Monitoring",        false, true,  true,  true],
  ["Proactive Device Maintenance",    false, true,  true,  true],
  ["Ransomware Detection & Response", false, true,  true,  true],
  ["24/7 SOC Threat Monitoring",      false, true,  true,  true],
  ["Helpdesk Support",                false, false, true,  true],
  ["M365 Intune & Defender Mgmt",     false, false, false, true],
  ["Microsoft 365 Maintenance",       false, false, false, true],
  ["Dedicated Account Manager",       false, false, false, true],
  ["Phishing Campaign & Training",    false, false, false, true],
  ["Dark Web Monitoring",             false, false, false, true],
];

export const TIERS = [
  { key: "Copper Defender", label: "Copper",        price: "$15/dev",             cellBg: "#fafafa" },
  { key: "Bronze Sentinel", label: "Bronze",        price: "$49/dev",             cellBg: null },
  { key: "Silver Guardian", label: "Silver",        price: "$49/dev + $95/usr",   cellBg: SILVER.columnTint },
  { key: "Gold Fortress",   label: "Gold Fortress", price: "$49/dev + $120/usr",  cellBg: "#fafafa" },
];

function ComparisonPage({ c }) {
  return (
    <div className="page" style={PAGE_BASE}>
      <HeaderBar customerName={c.customerName} accent={SILVER.accentSoft} />
      <GhostNumber n="05" />
      <div style={{ padding: "44px 64px 52px", position: "relative", minHeight: PAGE_BODY_H }}>
        <SectionLabel text="Section 05" accent={SILVER.accentSoft} />
        <h2 style={{ fontSize: 28, fontWeight: 800, color: "#1a1a1a", margin: "0 0 6px",
                     letterSpacing: "-0.02em" }}>Service Tier Comparison</h2>
        <p style={{ fontSize: 13.5, color: "#4b5563", margin: "0 0 22px", lineHeight: 1.5 }}>
          {/* ⚠ NOT "All plans include device management and remote access" — that
              sentence stood here until 2026-08-29 and, once Copper's row was
              corrected, it contradicted the two dashes printed directly beneath
              it. Copper is endpoint security only; managed support starts at
              Bronze. A standfirst that disagrees with its own table is worse
              than none, and this one sat one line above the evidence. */}
          Bronze and above include device management and remote access. Higher tiers add
          greater security depth and account management.
        </p>

        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12.5 }}>
          <thead>
            <tr>
              <th style={{ textAlign: "left", padding: "13px 16px", background: "#1a1a1a",
                           color: "rgba(255,255,255,0.6)", fontWeight: 500, fontSize: 11,
                           width: "36%", borderBottom: `3px solid ${SILVER.accentSoft}` }}>Feature</th>
              {TIERS.map((tier) => {
                const isRec = c.recommendedPlan === tier.key;
                return (
                  <th key={tier.key}
                      style={{ textAlign: "center", padding: "10px 8px", background: "#1a1a1a",
                               width: "16%" }}>
                    <div style={{ fontSize: 10, fontWeight: 700,
                                  color: isRec ? SILVER.accentSoft : "rgba(255,255,255,0.5)",
                                  letterSpacing: "0.05em", textTransform: "uppercase",
                                  marginBottom: 3 }}>
                      {tier.label}
                    </div>
                    <div style={{ fontSize: 10, fontWeight: 500,
                                  color: isRec ? "rgba(255,255,255,0.4)" : "rgba(255,255,255,0.35)" }}>
                      {tier.price}
                    </div>
                    {isRec && (
                      <div style={{ background: SILVER.accent, color: "white", fontSize: 8.5,
                                    padding: "2px 6px", borderRadius: 3, marginTop: 5,
                                    display: "inline-block", letterSpacing: "0.06em",
                                    textTransform: "uppercase", fontWeight: 700 }}>
                        Recommended
                      </div>
                    )}
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {FEATURE_MATRIX.map(([label, ...flags]) => (
              <tr key={label} style={{ borderBottom: "1px solid #f3f4f6" }}>
                <td style={{ padding: "8px 16px", fontSize: 12.5, color: "#1f2937", fontWeight: 500 }}>
                  {label}
                </td>
                {flags.map((on, i) => {
                  /* The recommended column is tinted all the way down and its
                     ticks are a shade darker, so the eye follows one column
                     rather than hunting for the badge at the top. Derived from
                     recommendedPlan, not hardcoded to Silver — the tier is
                     changeable per proposal, and a tint pinned to a column
                     would highlight the wrong one the moment it changed. */
                  const isRec = c.recommendedPlan === TIERS[i].key;
                  return (
                    <td key={TIERS[i].key}
                        style={{ textAlign: "center", padding: "8px 8px",
                                 background: isRec ? SILVER.columnTint
                                                   : (TIERS[i].cellBg || undefined) }}>
                      {on
                        ? <span style={{ color: isRec ? SILVER.tick : SILVER.accentSoft,
                                         fontWeight: 800, fontSize: 14 }}>✓</span>
                        : <span style={{ color: "#e5e7eb", fontSize: 13 }}>—</span>}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>

        <p style={{ fontSize: 11.5, color: "#6b7280", margin: "16px 0 0", fontStyle: "italic" }}>
          All prices are exclusive of GST. Requires Microsoft 365 Premium licence for Silver
          Guardian and Gold Fortress. Optional add-ons available — ask your account manager.
        </p>
      </div>
      <PageFooter />
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════════════
   PAGE 7 — Your IT Investment
   The tightest page in the document. The row heights below are the ones
   Bronze measured against live DOM; do not loosen the padding without
   re-measuring, because the packer budgets against these numbers.
   ══════════════════════════════════════════════════════════════════════ */
const IH = {
  band:        29,
  bandAction:  37,
  device:      51,
  user:        51,
  copper:      59,   // toggle row: title, count + rate line
  copperEdit:  63,   // ...with the rate as an input, which is taller
  bronzeAddOn: 59,   // ...and the same shape again
  m365premium: 40,   // the savings pill moved to its own aggregate box
  m365plan:    62,
  m365ItemEdit:44,
  m365ItemEditWarn:66,
  m365ItemPrint:40,
  m365Saving:  50,
  threeCXRow:  40,
  threeCXRowEdit:44,
  m365standard:50,
  network:     59,
  backup:      52,
  spam:        68,
  signature:   52,
  addlHead:    22,
  addlRowEdit: 36,
  addlRowPrint:23,
  spacer:      14,
  totalBar:    42,
  onceGap:     10,   // breathing room above the One-Off Costs band
  onceRow:     52,
  onceRowBadge:78,
  notes:       58,
};

const INVESTMENT_INTRO_H = 94;
const INVESTMENT_CAPACITY = 980;

const addlHead = {
  fontSize: 9, fontWeight: 700, color: "#6b7280", textTransform: "uppercase",
  letterSpacing: "0.08em", padding: "6px 8px",
};
const addlInput = {
  background: "#f8fafc", border: "1px solid #e5e7eb", borderRadius: 4, padding: "5px 8px",
  fontSize: 13, fontFamily: "inherit", color: "#1a1a1a", outline: "none", width: "100%",
  boxSizing: "border-box",
};

/* Silver never waives these fees BY DEFAULT — the amber badge is upsell copy
   pointing at Gold, not a concession Silver is making, which is why it shows
   statically here exactly as it does on Bronze.

   The waiver mechanism itself is still honoured, because the settings pane's
   toggle is generic across every tier. Rendering a static amount beside it
   would mean flipping that toggle silently dropped the Once-Off Total while
   the line items still read full price — a customer document whose numbers do
   not add up. Default false, correct if changed. */
function FeeValue({ amount, waived }) {
  if (!waived) {
    return (
      <p style={{ fontSize: 16, fontWeight: 700, color: "#1a1a1a", margin: 0, whiteSpace: "nowrap" }}>
        {fmtC(amount)}
      </p>
    );
  }
  return (
    <div style={{ whiteSpace: "nowrap" }}>
      <p style={{ fontSize: 12, color: "#9ca3af", margin: "0 0 1px",
                  textDecoration: "line-through" }}>{fmtC(amount)}</p>
      <p style={{ fontSize: 16, fontWeight: 700, color: "#1a1a1a", margin: "0 0 3px" }}>{fmtC(0)}</p>
      <span style={{ fontSize: 9, fontWeight: 700, letterSpacing: "0.08em",
                     textTransform: "uppercase", color: "#15803d", background: "#f0fdf4",
                     border: "1px solid #bbf7d0", padding: "1px 7px", borderRadius: 20 }}>
        Waived
      </span>
    </div>
  );
}

function WaivedBadge() {
  return (
    <span style={{ fontSize: 11, color: "#b45309", fontWeight: 600, background: "#fffbeb",
                   border: "1px solid #fde68a", padding: "2px 9px", borderRadius: 20,
                   display: "inline-block" }}>
      <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor"
           strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"
           style={{ verticalAlign: "middle", marginRight: 5 }}>
        <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
      </svg>
      Fee waived when proceeding with Gold Fortress
    </span>
  );
}

function InvestmentPage({ c, mode, set, t }) {
  const rows = Array.isArray(c.additionalRows) ? c.additionalRows : [];
  const patchRow = (i, p) => set({ additionalRows: rows.map((r, j) => (j === i ? { ...r, ...p } : r)) });
  const delRow = (i) => set({ additionalRows: rows.filter((_, j) => j !== i) });
  const addRow = () => set({ additionalRows: [...rows, { description: "", qty: "1", unitCost: "" }] });
  const interactive = mode === "edit";

  /* Extra Microsoft 365 licences — see the identical block in BronzeTemplate.
     `annualUnitPrice` is absent from a new row rather than "": most entries
     never have one, and it only matters to the annual-saving aggregate. */
  const m365Items = Array.isArray(c.additionalM365Items) ? c.additionalM365Items : [];
  const patchM365Item = (i, p) =>
    set({ additionalM365Items: m365Items.map((r, j) => (j === i ? { ...r, ...p } : r)) });
  const delM365Item = (i) => set({ additionalM365Items: m365Items.filter((_, j) => j !== i) });
  const addM365Item = () =>
    set({ additionalM365Items: [...m365Items, { description: "", quantity: "1", monthlyUnitPrice: "" }] });

  const seg = [];
  const push = (key, h, node, keepWithNext = false) => seg.push({ key, h, node, keepWithNext });

  push("band-monthly", IH.band,
    <SectionBand key="bm" title="Managed Services — Monthly" accent={SILVER.accent} />, true);

  push("device", IH.device, (
    <div key="dev" style={{ padding: "8px 24px", borderBottom: "1px solid #f3f4f6" }}>
      <SplitRow
        left={
          <>
            <p style={{ fontSize: 14, fontWeight: 600, color: "#1a1a1a", margin: "0 0 2px" }}>
              {c.recommendedPlan} — Devices
            </p>
            <p style={{ fontSize: 12, color: "#6b7280", margin: 0 }}>
              {t.deviceCount} devices × {fmt(t.perDevRate)}/mth
            </p>
          </>
        }
        right={
          <p style={{ fontSize: 16, fontWeight: 700, color: "#1a1a1a", margin: 0 }}>
            {fmt(t.monthlyDeviceCost)}/mth
          </p>
        }
      />
    </div>
  ));

  /* The per-user line. Guarded on the RATE, not on the tier: `monthlyUserCost`
     is always in `monthlyTotal`, so a tier that charges per user and does not
     print this row shows a total the customer cannot reconcile from the lines
     above it — it would drop the larger half of Silver's monthly bill from the
     page while still charging for it. That is what Gold did with $960/mth
     before this guard landed in all three templates. */
  if (t.perUsrRate > 0) push("user", IH.user, (
    <div key="usr" style={{ padding: "8px 24px", borderBottom: "1px solid #f3f4f6" }}>
      <SplitRow
        left={
          <>
            <p style={{ fontSize: 14, fontWeight: 600, color: "#1a1a1a", margin: "0 0 2px" }}>
              {c.recommendedPlan} — Users
            </p>
            <p style={{ fontSize: 12, color: "#6b7280", margin: 0 }}>
              {t.userCount} users × {fmt(t.perUsrRate)}/mth
            </p>
          </>
        }
        right={
          <p style={{ fontSize: 16, fontWeight: 700, color: "#1a1a1a", margin: 0 }}>
            {fmt(t.monthlyUserCost)}/mth
          </p>
        }
      />
    </div>
  ));

  push("band-m365", interactive ? IH.bandAction : IH.band,
    <SectionBand key="bm365" title="Microsoft 365 Subscription" accent={SILVER.accent}
                 extraStyle={{ borderTop: "1px solid #e5e7eb" }}
                 action={interactive ? (
                   <div onClick={addM365Item} className="no-print"
                        style={{ display: "inline-block", cursor: "pointer", padding: "4px 11px",
                                 background: SILVER.accent, borderRadius: 4 }}>
                     <span style={{ fontSize: 10, fontWeight: 700, color: "#ffffff",
                                    letterSpacing: "0.06em", textTransform: "uppercase" }}>
                       + Add Licence
                     </span>
                   </div>
                 ) : null} />, true);

  /* Plan choice. Editor-only: the finished document shows ONLY the selected
     plan, because a proposal listing both reads as a comparison the customer is
     being asked to resolve, and only one of them can ever be in the total. */
  if (interactive) push("m365-plan", IH.m365plan, (
    <div key="m365pick" className="no-print"
         style={{ padding: "8px 24px", borderBottom: "1px solid #f3f4f6", background: "#f8fafc" }}>
      {Object.values(M365_PLANS).map((p) => (
        <div key={p.key} onClick={() => set({ m365PlanChoice: p.key })}
             style={{ display: "block", cursor: "pointer", padding: "3px 0" }}>
          <table style={{ borderCollapse: "collapse" }}><tbody><tr>
            <td style={{ padding: 0, paddingRight: 10, width: 16, verticalAlign: "middle" }}>
              <span style={{ display: "inline-block", width: 13, height: 13, borderRadius: "50%",
                             border: `1px solid ${t.m365PlanChoice === p.key ? SILVER.accent : "#cbd5e1"}`,
                             background: t.m365PlanChoice === p.key ? SILVER.accent : "#ffffff",
                             boxShadow: t.m365PlanChoice === p.key ? "inset 0 0 0 2.5px #ffffff" : "none" }} />
            </td>
            <td style={{ padding: 0, verticalAlign: "middle" }}>
              <span style={{ fontSize: 12.5,
                             fontWeight: t.m365PlanChoice === p.key ? 700 : 500,
                             color: t.m365PlanChoice === p.key ? "#1a1a1a" : "#6b7280" }}>
                {p.label}
              </span>
              <span style={{ fontSize: 11, color: "#6b7280", marginLeft: 8 }}>
                {fmt2(p.monthly)}/user/mth · {fmt2(p.annual)} annualised
              </span>
            </td>
          </tr></tbody></table>
        </div>
      ))}
    </div>
  ), true);

  push("m365-premium", IH.m365premium, (
    <div key="m365p" style={{ padding: "8px 24px", borderBottom: "1px solid #f3f4f6" }}>
      <SplitRow
        left={
          <>
            <span style={{ fontSize: 14, fontWeight: 600, color: "#1a1a1a", marginRight: 10 }}>
              {t.m365Plan.label}
            </span>
            <span style={{ whiteSpace: "nowrap" }}>
              <NumberCell mode={mode} value={t.m365Qty}
                          onChange={(v) => set({ m365PremiumQty: v })} />
              <span style={{ fontSize: 12, color: "#6b7280", marginLeft: 5 }}>
                users × {fmt2(t.m365PremiumRate)}/user/mth
              </span>
            </span>
          </>
        }
        right={
          <p style={{ fontSize: 16, fontWeight: 700, color: "#1a1a1a", margin: 0,
                      whiteSpace: "nowrap" }}>{fmt2(t.m365PremiumMonthly)}/mth</p>
        }
      />
    </div>
  ));

  /* Pushed only when it will actually appear: a `hide-in-print` row still
     consumed its 50px of page budget, spending height on nothing. */
  if (c.showM365Standard || interactive) push("m365-standard", IH.m365standard, (
    <div key="m365s" className={c.showM365Standard ? "" : "hide-in-print"}
         style={{ padding: "8px 24px", borderBottom: "1px solid #e5e7eb", background: "#fafafa" }}>
      <SplitRow
        left={
          <table style={{ borderCollapse: "collapse" }}>
            <tbody>
              <tr>
                {mode !== "print" && (
                  <td style={{ verticalAlign: "middle", padding: 0, paddingRight: 14, width: 20 }}>
                    <CheckBox mode={mode} checked={!!c.showM365Standard} accent={SILVER.accent}
                              onToggle={() => set({ showM365Standard: !c.showM365Standard })} />
                  </td>
                )}
                <td style={{ verticalAlign: "middle", padding: 0 }}>
                  <p style={{ fontSize: 13, fontWeight: 500, color: "#4b5563", margin: "0 0 2px" }}>
                    Microsoft 365 Business Standard{" "}
                    <span style={{ fontSize: 11, color: "#6b7280", fontWeight: 500, fontStyle: "italic" }}>
                      — current plan, not added to total
                    </span>
                  </p>
                  <p style={{ fontSize: 12, color: "#6b7280", margin: 0 }}>
                    {t.userCount} users × $22.44/user/mth = {fmt2(t.m365StandardMonthly)}/mth
                  </p>
                </td>
              </tr>
            </tbody>
          </table>
        }
        right={
          <span style={{ fontSize: 11, color: SILVER.tick, fontWeight: 600, background: SILVER.columnTint,
                         border: "1px solid #cbd5e1", padding: "3px 10px", borderRadius: 20,
                         whiteSpace: "nowrap" }}>
            Net upgrade cost: +{fmt2(t.m365NetUpgrade)}/mth
          </span>
        }
      />
    </div>
  ));

  /* Freeform extra licences. Each row is its own segment so a long list flows
     across pages rather than forcing the card onto one. */
  m365Items.forEach((r, i) => {
    const qty = parseFloat(r.quantity) || 0;
    const rowTotal = qty * (parseFloat(r.monthlyUnitPrice) || 0);
    const hasAnnual = Number.isFinite(parseFloat(r.annualUnitPrice));
    const suspect = m365ItemRateSuspect(r);
    push(`m365item-${i}`,
         interactive ? (suspect ? IH.m365ItemEditWarn : IH.m365ItemEdit) : IH.m365ItemPrint, (
      <div key={`m365i${i}`} style={{ padding: "8px 24px", borderBottom: "1px solid #f3f4f6" }}>
        <SplitRow
          left={interactive ? (
            <table style={{ borderCollapse: "collapse", width: "100%" }}><tbody><tr>
              <td style={{ padding: "0 8px 0 0" }}>
                <input type="text" value={r.description || ""}
                       placeholder="e.g. Microsoft 365 Online Exchange Plan 1"
                       onChange={(e) => patchM365Item(i, { description: e.target.value })}
                       style={addlInput} />
              </td>
              <td style={{ padding: "0 8px 0 0", width: 58 }}>
                <input type="number" min="0" value={r.quantity || ""} placeholder="Qty"
                       onChange={(e) => patchM365Item(i, { quantity: e.target.value })}
                       style={{ ...addlInput, textAlign: "center" }} />
              </td>
              <td style={{ padding: "0 8px 0 0", width: 90 }}>
                <MoneyInput value={r.monthlyUnitPrice} ariaLabel="Monthly unit price"
                            inputStyle={addlInput}
                            onCommit={(v) => patchM365Item(i, { monthlyUnitPrice: v })} />
              </td>
              <td style={{ padding: 0, width: 90 }}>
                {/* Optional. Left blank the item simply does not take part in the
                    annual-saving comparison — there is no annual price to claim
                    a saving against, and inventing one would overstate it. */}
                <MoneyInput value={r.annualUnitPrice} ariaLabel="Annual-commitment unit price"
                            inputStyle={addlInput} invalid={suspect}
                            onCommit={(v) => patchM365Item(i, { annualUnitPrice: v })} />
              </td>
              <td style={{ padding: "0 0 0 8px", width: 28 }}>
                <div className="no-print" onClick={() => delM365Item(i)}
                     style={{ width: 24, height: 24, textAlign: "center", lineHeight: "24px",
                              cursor: "pointer", borderRadius: 4, background: "#fef2f2" }}>
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#ef4444"
                       strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
                  </svg>
                </div>
              </td>
            </tr>
            {suspect && (
              /* Editor-only. The row still bills normally; it is only left out
                 of the saving. Said out loud because a silent exclusion is how
                 the wrong number reaches a customer. */
              <tr><td colSpan={5} style={{ padding: "4px 0 0" }}>
                <span style={{ fontSize: 10.5, color: "#b91c1c" }}>
                  Annual rate is higher than the monthly rate, so this line is left out of the
                  saving. Enter the per-month rate under annual commitment, not the yearly total.
                </span>
              </td></tr>
            )}
            </tbody></table>
          ) : (
            <>
              <p style={{ fontSize: 14, fontWeight: 600, color: "#1a1a1a", margin: "0 0 2px" }}>
                {r.description}
              </p>
              <p style={{ fontSize: 12, color: "#6b7280", margin: 0 }}>
                {qty} × {fmt2(parseFloat(r.monthlyUnitPrice) || 0)}/mth
                {hasAnnual && !suspect
                  ? ` · ${fmt2(parseFloat(r.annualUnitPrice))}/mth on annual commitment` : ""}
              </p>
            </>
          )}
          right={interactive ? null : (
            <p style={{ fontSize: 16, fontWeight: 700, color: "#1a1a1a", margin: 0,
                        whiteSpace: "nowrap" }}>{fmt2(rowTotal)}/mth</p>
          )}
        />
      </div>
    ));
  });

  /* The annual-commitment saving, for the WHOLE section rather than per line.
     Suppressed at zero: "Save $0/yr" is not an offer. */
  if (t.m365TotalAnnualSaving > 0) push("m365-saving", IH.m365Saving, (
    <div key="m365save" style={{ padding: "9px 24px", borderBottom: "1px solid #e5e7eb",
                                 background: "#f0fdf4" }}>
      <SplitRow
        left={
          <>
            <p style={{ fontSize: 13, fontWeight: 700, color: "#059669", margin: "0 0 2px" }}>
              Save {fmt(t.m365TotalAnnualSaving)}/yr switching to annual commitment
            </p>
            <p style={{ fontSize: 11, color: "#6b7280", margin: 0 }}>
              Across the Microsoft 365 subscriptions above, compared with month-to-month pricing.
            </p>
          </>
        }
        right={null}
      />
    </div>
  ));

  /* 3CX Phone System. Its own section between Microsoft 365 and the add-ons.
     Excluded, the whole thing LEAVES the document — it is not merely hidden and
     it contributes nothing to the total, so an amount typed and then unticked
     cannot linger in a figure the customer is asked to reconcile. It is still
     shown while editing, unticked, or there would be no way to turn it on. */
  if (t.include3CX || interactive) {
    push("band-3cx", interactive ? IH.bandAction : IH.band, (
      <SectionBand key="b3cx" title="3CX Phone System" accent={SILVER.accent}
                   hint={interactive && !t.include3CX ? "— not included" : undefined}
                   extraStyle={{ borderTop: "1px solid #e5e7eb" }}
                   action={interactive ? (
                     <span className="no-print" onClick={() => set({ include3CX: !c.include3CX })}
                           style={{ cursor: "pointer", display: "inline-block" }}>
                       <table style={{ borderCollapse: "collapse" }}><tbody><tr>
                         <td style={{ padding: 0, paddingRight: 8, verticalAlign: "middle" }}>
                           <CheckBox mode={mode} checked={t.include3CX}
                                     onToggle={() => set({ include3CX: !c.include3CX })} />
                         </td>
                         <td style={{ padding: 0, verticalAlign: "middle" }}>
                           <span style={{ fontSize: 10, fontWeight: 700, color: "#ffffff",
                                          letterSpacing: "0.06em", textTransform: "uppercase" }}>
                             Include
                           </span>
                         </td>
                       </tr></tbody></table>
                     </span>
                   ) : null} />
    ), true);

    push("3cx-hosting", interactive ? IH.threeCXRowEdit : IH.threeCXRow, (
      <div key="3cxh" style={{ padding: "8px 24px", borderBottom: "1px solid #f3f4f6",
                               opacity: t.include3CX ? 1 : 0.45 }}>
        <SplitRow
          left={
            <p style={{ fontSize: 14, fontWeight: 600, color: "#1a1a1a", margin: 0 }}>
              Monthly Hosting &amp; Management
            </p>
          }
          right={interactive ? (
            <span style={{ display: "inline-block", width: 104 }}>
              <MoneyInput value={c.threeCXHostingFee} ariaLabel="3CX monthly hosting fee"
                          inputStyle={addlInput}
                          onCommit={(v) => set({ threeCXHostingFee: v })} />
            </span>
          ) : (
            <p style={{ fontSize: 16, fontWeight: 700, color: "#1a1a1a", margin: 0,
                        whiteSpace: "nowrap" }}>{fmt2(t.threeCXHostingFee)}/mth</p>
          )}
        />
      </div>
    ));

    push("3cx-sip", interactive ? IH.threeCXRowEdit : IH.threeCXRow, (
      <div key="3cxs" style={{ padding: "8px 24px", borderBottom: "1px solid #e5e7eb",
                               opacity: t.include3CX ? 1 : 0.45 }}>
        <SplitRow
          left={interactive ? (
            <table style={{ borderCollapse: "collapse", width: "100%" }}><tbody><tr>
              <td style={{ padding: 0, paddingRight: 8, whiteSpace: "nowrap", verticalAlign: "middle" }}>
                <span style={{ fontSize: 14, fontWeight: 600, color: "#1a1a1a" }}>
                  SIP Trunk Service —
                </span>
              </td>
              <td style={{ padding: 0, verticalAlign: "middle" }}>
                {/* Freeform on purpose: "3 Line Service" is not a number. */}
                <input type="text" value={c.threeCXSipTrunkDescription || ""}
                       placeholder="e.g. 3 Line Service"
                       onChange={(e) => set({ threeCXSipTrunkDescription: e.target.value })}
                       style={addlInput} />
              </td>
            </tr></tbody></table>
          ) : (
            <p style={{ fontSize: 14, fontWeight: 600, color: "#1a1a1a", margin: 0 }}>
              SIP Trunk Service
              {t.threeCXSipTrunkDescription ? ` — ${t.threeCXSipTrunkDescription}` : ""}
            </p>
          )}
          right={interactive ? (
            <span style={{ display: "inline-block", width: 104 }}>
              <MoneyInput value={c.threeCXSipTrunkPrice} ariaLabel="3CX SIP trunk price"
                          inputStyle={addlInput}
                          onCommit={(v) => set({ threeCXSipTrunkPrice: v })} />
            </span>
          ) : (
            <p style={{ fontSize: 16, fontWeight: 700, color: "#1a1a1a", margin: 0,
                        whiteSpace: "nowrap" }}>{fmt2(t.threeCXSipTrunkPrice)}/mth</p>
          )}
        />
      </div>
    ));
  }

  /* Hidden outright when nothing under it is selected — a heading over an
     empty space reads as content that failed to load. The hint is an
     instruction to the sender, so it never belongs on a customer document. */
  /* ⚠ EVERY toggle that can push a row into this section must be listed here,
     or the row prints with no heading above it. Copper and the Bronze grouping
     were added to the rows and missed here first time: both rendered, the
     "Optional Add-Ons" band did not, and the page carried two priced lines
     under whatever heading happened to precede them. */
  const anyAddOn = !!(c.addOnNetwork || c.addOnBackup || c.addOnSpamThreat
                      || c.addOnEmailSignature || c.addOnCopper || c.addOnBronze);
  if (anyAddOn || interactive)
  push("band-addons", IH.band,
    <SectionBand key="ba" title="Optional Add-Ons" hint={interactive ? "— tick to include in monthly total" : undefined}
                 accent={SILVER.accent}
                 extraStyle={{ borderTop: "1px solid #e5e7eb" }} />, true);

  /* An add-on the customer did not buy prints NOTHING — no row, no
     "Not selected", no $0.00. Four unselected add-ons were costing ~230px of a
     page that clips rather than reflows. Still rendered while editing, or there
     would be no way to tick one. */
  const addOnRow = (key, h, checked, onToggle, title, sub, note, cost, last) => {
    if (!checked && !interactive) return;
    return push(key, h, (
    <div key={key} style={{ padding: "8px 24px",
                            borderBottom: `1px solid ${last ? "#e5e7eb" : "#f3f4f6"}` }}>
      <SplitRow
        left={
          <table style={{ borderCollapse: "collapse" }}>
            <tbody>
              <tr>
                {mode !== "print" && (
                  <td style={{ verticalAlign: "middle", padding: 0, paddingRight: 14, width: 20 }}>
                    <CheckBox mode={mode} checked={checked} onToggle={onToggle}
                              accent={SILVER.accent} />
                  </td>
                )}
                <td style={{ verticalAlign: "middle", padding: 0 }}>
                  <p style={{ fontSize: 14, fontWeight: 600, color: "#1a1a1a", margin: "0 0 3px" }}>
                    {title}
                  </p>
                  <p style={{ fontSize: 12, color: "#6b7280", margin: 0 }}>{sub}</p>
                  {note && (
                    <p style={{ fontSize: 11, color: "#9ca3af", margin: "2px 0 0" }}>{note}</p>
                  )}
                </td>
              </tr>
            </tbody>
          </table>
        }
        right={
          checked
            ? <p style={{ fontSize: 16, fontWeight: 700, color: "#1a1a1a", margin: 0,
                          whiteSpace: "nowrap" }}>{cost}/mth</p>
            : <p style={{ fontSize: 13, color: "#9ca3af", margin: 0, whiteSpace: "nowrap" }}>
                Not selected
              </p>
        }
      />
    </div>
  ));
  };

  /* ⚠ Placed HERE, with the other add-on rows, and not beside the device row
     above where they belong visually — `addOnRow` is a const declared further
     down this function, so calling it earlier is a temporal-dead-zone error
     that the minifier turns into a production-only "Cannot access before
     initialization". */
  addOnRow("copper", interactive ? IH.copperEdit : IH.copper, !!c.addOnCopper,
    () => set({ addOnCopper: !c.addOnCopper }),
    <>Copper Defender &mdash; Devices{" "}
      <span style={{ whiteSpace: "nowrap", fontWeight: 400 }}>
        <NumberCell mode={mode} min={0} value={t.copperDeviceCount}
                    onChange={(v) => set({ copperDeviceCount: v })} />
        <span style={{ fontSize: 12, color: "#6b7280", marginLeft: 5 }}>devices</span>
      </span>
    </>,
    /* ⚠ An editable RATE, not a caption, and therefore a MoneyInput and not
       a NumberCell: NumberCell parses with parseInt and floors at 1, so it
       cannot express $15.50 and would silently rewrite it to 15. MoneyInput is
       the same control the 3CX fee and the M365 unit prices already use —
       type="text" inputMode="decimal", a $ prefix, the raw string held while
       focused and 2dp on blur. A numeric field seeded with a literal 0 is what
       produced the `010` display bug.
       Printed with fmt2, not fmt: fmt drops the cents, so a $15.50 rate would
       reach a customer document reading "$15.5". */
    interactive ? (
      <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
        <span style={{ display: "inline-block", width: 88 }}>
          <MoneyInput value={c.copperRate} ariaLabel="Copper Defender device rate"
                      inputStyle={addlInput}
                      onCommit={(v) => set({ copperRate: v })} />
        </span>
        <span>/device/mth</span>
      </span>
    ) : `${fmt2(t.copperRate)}/device/mth`,
    null, fmt(t.copperCost));

  /* Bronze devices on a Silver or Gold proposal. Never offered on Bronze
     itself, which IS the base tier — computeBronzeTotals refuses to charge for
     it there too, so the row and the total cannot disagree. */
  if (!t.isBronzeTier) addOnRow("bronzeAddOn", IH.bronzeAddOn, !!c.addOnBronze,
    () => set({ addOnBronze: !c.addOnBronze }),
    <>Bronze Sentinel &mdash; Devices{" "}
      <span style={{ whiteSpace: "nowrap", fontWeight: 400 }}>
        <NumberCell mode={mode} min={0} value={t.bronzeDeviceCount}
                    onChange={(v) => set({ bronzeDeviceCount: v })} />
        <span style={{ fontSize: 12, color: "#6b7280", marginLeft: 5 }}>devices</span>
      </span>
    </>,
    `${fmt(t.perDevRate)}/device/mth — same rate as ${c.recommendedPlan} devices`,
    null, fmt(t.bronzeAddOnCost));

  addOnRow("network", IH.network, !!c.addOnNetwork,
    () => set({ addOnNetwork: !c.addOnNetwork }),
    <>Network Monitor &amp; Maintenance{" "}
      <span style={{ whiteSpace: "nowrap", fontWeight: 400 }}>
        <NumberCell mode={mode} value={t.networkDeviceCount}
                    onChange={(v) => set({ networkDeviceCount: v })} />
        <span style={{ fontSize: 12, color: "#6b7280", marginLeft: 5 }}>network devices</span>
      </span>
    </>,
    // isHighTier already covers Silver — computeBronzeTotals treats Silver and
    // Gold alike for this rate, which is why nothing tier-specific is needed.
    `${fmt(t.networkRate)}/device/mth — ${t.isHighTier ? "Gold & Silver rate" : "Copper & Bronze rate"}`,
    null, fmt(t.networkCost));

  /* What the Network Monitor add-on actually covers. Descriptive only — it never
     touches a total — and printed only when the add-on is included, since a
     device list under a service the customer did not buy reads as one they are
     getting. Identical in all three templates. */
  if (c.addOnNetwork && (interactive || String(c.networkMonitorDevicesCovered || "").trim()))
    push("network-covered", networkCoveredH(c.networkMonitorDevicesCovered, interactive), (
    <div key="netcov" style={{ padding: "0 24px 9px 24px", borderBottom: "1px solid #f3f4f6",
                               background: "#ffffff" }}>
      <p style={{ fontSize: 10, fontWeight: 700, color: "#9ca3af", letterSpacing: "0.06em",
                  textTransform: "uppercase", margin: "0 0 3px" }}>
        Devices covered
      </p>
      {interactive ? (
        <textarea value={c.networkMonitorDevicesCovered || ""}
                  placeholder="e.g. 2 × FortiGate 60F, 4 × UniFi switches, 6 × access points"
                  onChange={(e) => set({ networkMonitorDevicesCovered: e.target.value })}
                  rows={2}
                  style={{ ...addlInput, width: "100%", resize: "vertical", lineHeight: 1.5 }} />
      ) : (
        <p style={{ fontSize: 12, color: "#4b5563", margin: 0, lineHeight: 1.5,
                    whiteSpace: "pre-wrap" }}>
          {c.networkMonitorDevicesCovered}
        </p>
      )}
    </div>
  ));

  addOnRow("backup", IH.backup, !!c.addOnBackup,
    () => set({ addOnBackup: !c.addOnBackup }),
    "Microsoft 365 End User Backup",
    `${t.userCount} users × ${fmt(t.backupRate)}/user/mth — email, OneDrive & SharePoint backup`,
    null, fmt(t.userCount * t.backupRate));

  addOnRow("spam", IH.spam, !!c.addOnSpamThreat,
    () => set({ addOnSpamThreat: !c.addOnSpamThreat }),
    <>Advanced AI Spam &amp; Threat Protection</>,
    `${t.userCount} users × ${fmt(t.spamThreatRate)}/user/mth`,
    "Replaces traditional Spam & Threat Protection service", fmt(t.spamThreatCost));

  addOnRow("signature", IH.signature, !!c.addOnEmailSignature,
    () => set({ addOnEmailSignature: !c.addOnEmailSignature }),
    "Email Signature Management",
    `${t.userCount} users × ${fmt(t.emailSignatureRate)}/user/mth`,
    null, fmt(t.emailSignatureCost), true);

  push("band-additional", IH.bandAction, (
    <SectionBand key="badd" title="Additional Monthly Charges" accent={SILVER.accent}
                 extraStyle={{ borderTop: "1px solid #e5e7eb" }}
                 action={interactive ? (
                   <div onClick={addRow} className="no-print"
                        style={{ display: "inline-block", cursor: "pointer", padding: "4px 11px",
                                 background: SILVER.accent, borderRadius: 4 }}>
                     <span style={{ fontSize: 10, fontWeight: 700, color: "#ffffff",
                                    letterSpacing: "0.06em", textTransform: "uppercase" }}>
                       + Add Row
                     </span>
                   </div>
                 ) : null} />
  ), true);

  if (rows.length > 0) {
    push("addl-head", IH.addlHead, (
      <table key="ah" style={{ width: "100%", borderCollapse: "collapse" }}>
        <thead>
          <tr style={{ background: "#f8fafc", borderBottom: "1px solid #f3f4f6" }}>
            <th style={{ ...addlHead, textAlign: "left", paddingLeft: 24 }}>Description</th>
            <th style={{ ...addlHead, textAlign: "center", width: 70 }}>Qty</th>
            <th style={{ ...addlHead, textAlign: "right", width: 90 }}>Unit Cost</th>
            <th style={{ ...addlHead, textAlign: "right", width: 90 }}>Total</th>
            {interactive && <th style={{ ...addlHead, width: 32, paddingRight: 24 }} />}
          </tr>
        </thead>
      </table>
    ), true);

    rows.forEach((r, i) => {
      const rowTotal = (parseFloat(r.qty) || 0) * (parseFloat(r.unitCost) || 0);
      push(`addl-${i}`, interactive ? IH.addlRowEdit : IH.addlRowPrint, (
        <table key={`ar${i}`} style={{ width: "100%", borderCollapse: "collapse" }}>
          <tbody>
            <tr style={{ borderBottom: "1px solid #f3f4f6" }}>
              <td style={{ padding: "6px 8px 6px 24px", verticalAlign: "middle" }}>
                {interactive ? (
                  <input type="text" value={r.description || ""} placeholder="e.g. Firewall Subscription"
                         onChange={(e) => patchRow(i, { description: e.target.value })}
                         style={addlInput} />
                ) : (
                  <span style={{ fontSize: 13, color: "#1a1a1a" }}>{r.description}</span>
                )}
              </td>
              <td style={{ padding: "6px 8px", textAlign: "center", verticalAlign: "middle", width: 70 }}>
                {interactive ? (
                  <input type="number" min="0" value={r.qty || ""}
                         onChange={(e) => patchRow(i, { qty: e.target.value })}
                         style={{ ...addlInput, textAlign: "center" }} />
                ) : (
                  <span style={{ fontSize: 13, color: "#1a1a1a" }}>{r.qty}</span>
                )}
              </td>
              <td style={{ padding: "6px 8px", textAlign: "right", verticalAlign: "middle", width: 90 }}>
                {interactive ? (
                  <input type="number" min="0" step="0.01" value={r.unitCost || ""} placeholder="0.00"
                         onChange={(e) => patchRow(i, { unitCost: e.target.value })}
                         style={{ ...addlInput, textAlign: "right" }} />
                ) : (
                  <span style={{ fontSize: 13, color: "#1a1a1a" }}>
                    {r.unitCost ? fmt2(parseFloat(r.unitCost)) : ""}
                  </span>
                )}
              </td>
              <td style={{ padding: "6px 8px", textAlign: "right", verticalAlign: "middle", width: 90,
                           fontSize: 13, fontWeight: 600, color: "#1a1a1a", whiteSpace: "nowrap" }}>
                {"$" + rowTotal.toLocaleString("en-AU", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </td>
              {interactive && (
                <td style={{ padding: "6px 24px 6px 8px", verticalAlign: "middle", width: 32 }}>
                  <div className="no-print" onClick={() => delRow(i)}
                       style={{ width: 24, height: 24, textAlign: "center", lineHeight: "24px",
                                cursor: "pointer", borderRadius: 4, background: "#fef2f2" }}>
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#ef4444"
                         strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                      <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
                    </svg>
                  </div>
                </td>
              )}
            </tr>
          </tbody>
        </table>
      ));
    });
  } else {
    push("addl-spacer", IH.spacer, <div key="as" style={{ height: 14, background: "#ffffff" }} />);
  }

  /* Monthly bar stays TEAL — it is the family convention across Bronze, Gold
     and Silver's own source, and the once-off bar below is what carries the
     contrast. */
  push("monthly-total", IH.totalBar, (
    <div key="mt" style={{ background: TEAL, padding: "9px 24px" }}>
      <SplitRow
        left={<span style={{ fontSize: 12, fontWeight: 700, color: "rgba(255,255,255,0.9)",
                             letterSpacing: "0.08em", textTransform: "uppercase" }}>
                Monthly Total (ex GST)</span>}
        right={<span style={{ fontSize: 22, fontWeight: 800, color: "#ffffff",
                              letterSpacing: "-0.02em" }}>{fmtMth(t.monthlyTotal)}</span>}
      />
    </div>
  ));

  /* A small clean gap lifting the whole One-Off Costs section away from the
     Monthly Total bar, so the two halves of the page read as two things rather
     than one continuous run of bars. Deliberately small — 10px, less than the
     14px used before a total — since this separates sections, it does not
     announce a final figure. Bound to the band with keepWithNext so a page
     break can never leave the gap stranded at the foot of a page. */
  push("pre-once-gap", IH.onceGap,
    <div key="pog" style={{ height: 10, background: "#ffffff" }} />, true);
  push("band-once", IH.band,
    <SectionBand key="bo" title="One-Off Costs" accent={SILVER.accent}
                 extraStyle={{ borderTop: `2px solid ${SILVER.accent}` }} />, true);

  push("once-device", IH.onceRow, (
    <div key="od" style={{ padding: "8px 24px", borderBottom: "1px solid #f3f4f6" }}>
      <SplitRow
        align="top"
        left={
          <>
            <p style={{ fontSize: 14, fontWeight: 600, color: "#1a1a1a", margin: "0 0 3px" }}>
              Onboarding Fee (per device)
            </p>
            <p style={{ fontSize: 12, color: "#6b7280", margin: 0 }}>
              {t.deviceCount} devices × {fmt(t.intunePerDevRate)} — device enrolment &amp; configuration
            </p>
          </>
        }
        right={
          <p style={{ fontSize: 16, fontWeight: 700, color: "#1a1a1a", margin: 0, whiteSpace: "nowrap" }}>
            {fmtC(t.intunePerDevCost)}
          </p>
        }
      />
    </div>
  ));

  push("once-intune", IH.onceRowBadge, (
    <div key="oi" style={{ padding: "8px 24px", borderBottom: "1px solid #f3f4f6" }}>
      <SplitRow
        align="top"
        left={
          <>
            <p style={{ fontSize: 14, fontWeight: 600, color: "#1a1a1a", margin: "0 0 3px" }}>
              Intune Migration &amp; Setup
            </p>
            <p style={{ fontSize: 12, color: "#4b5563", margin: "0 0 6px" }}>
              Defender setup, policy configuration &amp; security baseline — fixed fee
            </p>
            {!t.waiveSetupFees && <WaivedBadge />}
          </>
        }
        right={<FeeValue amount={t.intuneSetup} waived={t.waiveSetupFees} />}
      />
    </div>
  ));

  push("once-onboarding", IH.onceRowBadge, (
    <div key="oo" style={{ padding: "8px 24px", borderBottom: "1px solid #e5e7eb" }}>
      <SplitRow
        align="top"
        left={
          <>
            <p style={{ fontSize: 14, fontWeight: 600, color: "#1a1a1a", margin: "0 0 3px" }}>
              Initial Onboarding
            </p>
            <p style={{ fontSize: 12, color: "#4b5563", margin: "0 0 6px" }}>
              Full service transition, documentation &amp; provider handover
            </p>
            {!t.waiveSetupFees && <WaivedBadge />}
          </>
        }
        right={<FeeValue amount={t.onboarding} waived={t.waiveSetupFees} />}
      />
    </div>
  ), true);

  /* Dark bar with a teal figure — Craig's call where the Silver source and his
     written spec disagreed. The source painted this bar teal too, leaving two
     identical bars stacked; Bronze and Gold both use the dark treatment and the
     spec described it, so the odd one out was the source. */
  push("pre-once-spacer", IH.spacer, <div key="ps" style={{ height: 14, background: "#ffffff" }} />);
  /* Identical treatment to Monthly Total — same bar colour, same weights,
     and the same 14px of breathing room before the final number. */
  push("once-total", IH.totalBar, (
    <div key="ot" style={{ background: TEAL, padding: "9px 24px" }}>
      <SplitRow
        left={<span style={{ fontSize: 12, fontWeight: 700, color: "rgba(255,255,255,0.9)",
                             letterSpacing: "0.08em", textTransform: "uppercase" }}>
                Once-Off Total (ex GST)</span>}
        /* WHITE, like Monthly Total's figure — not TEAL. The figure used to be
           teal on a near-black bar; once the bar became TEAL to match Monthly
           Total, a teal figure on it was invisible. Bronze and Gold spell the
           same colour as a literal, which is why only Silver was affected. */
        right={<span style={{ fontSize: 22, fontWeight: 800, color: "#ffffff",
                              letterSpacing: "-0.02em" }}>{fmtC(t.onceOffTotal)}</span>}
      />
    </div>
  ), true);

  push("notes", IH.notes, null);

  const pages = packSegments(seg, { firstOffset: INVESTMENT_INTRO_H,
                                    capacity: INVESTMENT_CAPACITY });

  return (
    <>
      {pages.map((pageSegs, pageIdx) => {
        const cardSegs = pageSegs.filter((s) => s.key !== "notes");
        const hasNotes = pageSegs.some((s) => s.key === "notes");
        return (
          <div key={pageIdx} className="page" data-flow="investment" style={PAGE_BASE}>
            <HeaderBar customerName={c.customerName} accent={SILVER.accentSoft} />
            <GhostNumber n={pageIdx === 0 ? "06" : `06.${pageIdx + 1}`} />
            <div style={{ padding: "30px 64px 48px", position: "relative",
                          minHeight: PAGE_BODY_H }}>
              {pageIdx === 0 ? (
                <>
                  <SectionLabel text="Section 06" accent={SILVER.accentSoft} />
                  <h2 style={{ fontSize: 26, fontWeight: 800, color: "#1a1a1a", margin: "0 0 4px",
                               letterSpacing: "-0.02em" }}>Your IT Investment</h2>
                  <p style={{ fontSize: 13, color: "#4b5563", margin: "0 0 16px", lineHeight: 1.45 }}>
                    A clear breakdown of your ongoing managed services and one-off setup costs
                    for {c.customerName}.
                  </p>
                </>
              ) : (
                <p style={{ fontSize: 11, color: "#9ca3af", fontStyle: "italic", margin: "0 0 16px" }}>
                  Your IT Investment (continued)
                </p>
              )}

              <div style={{ border: "1px solid #e5e7eb", borderRadius: 10, overflow: "hidden",
                            marginBottom: 14 }}>
                {cardSegs.map((s) => s.node)}
              </div>

              {hasNotes && (
                <BulletRow
                  gap={8}
                  bullet={
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#9ca3af"
                         strokeWidth="2" style={{ marginTop: 2 }}>
                      <circle cx="12" cy="12" r="10" /><line x1="12" y1="8" x2="12" y2="12" />
                      <line x1="12" y1="16" x2="12.01" y2="16" />
                    </svg>
                  }
                >
                  <p style={{ fontSize: 12, color: "#4b5563", margin: 0, lineHeight: 1.6 }}>
                    All pricing is exclusive of GST. Quantities are based on information provided
                    by {c.customerName} — adjustments may apply following our onboarding audit.
                    This proposal is valid for 30 days. Discounts are subject to signing before
                    30 days.
                  </p>
                </BulletRow>
              )}
            </div>
            <PageFooter />
          </div>
        );
      })}
    </>
  );
}

/* ══════════════════════════════════════════════════════════════════════
   PAGE 8 — Response Times & Service Terms
   ══════════════════════════════════════════════════════════════════════ */
const PRIORITY_ROWS = [
  ["#ef4444", "Critical", "Main server or a network switch is offline; a VPN link between offices has failed — all or most users unable to work.", "N/A", "30 Min", "15 Min"],
  ["#f97316", "High", "Internet connection is offline (local work still possible); a key device or accounting software has stopped working.", "N/A", "2 Hours", "1 Hour"],
  ["#eab308", "Medium", "A user's desktop won't turn on; a printer isn't working but another is available; a user can't connect to Wi-Fi.", "N/A", "4 Hours", "2 Hours"],
  ["#3b82f6", "Low", "Printing is slower than normal; a single user can't scan; a user needs a program installed on their PC.", "N/A", "12 Hours", "8 Hours"],
  ["#22c55e", "No Priority", "Pro-active maintenance of systems.", "N/A", "N/A", "N/A"],
];

const TERMS = [
  "Silver Guardian requires a Microsoft 365 Premium subscription — all devices must run Windows 11 Professional/Business. Charges apply to upgrade from Windows Home or Windows 10.",
  "'Unlimited Remote Support' applies during business hours, Mon–Fri 8:30am–5pm AWST. Requests lodged outside these hours are still actioned as fast as possible, on a best-effort basis, without the response time guarantee.",
  "Ad hoc on-site support and project work is charged at $170 ex GST per hour for Silver & Gold plans (Copper & Bronze plans are charged at $190 ex GST per hour), plus a $75 ex GST call-out fee per visit.",
  "A once-off onboarding fee applies to set up Intune and Defender and enrol all devices — $110 ex GST per device, plus a fixed $599 fee to configure your M365 Intune/Defender security policy suite.",
];

const thMuted = {
  fontSize: 10, fontWeight: 700, color: "#6b7280",
  letterSpacing: "0.08em", textTransform: "uppercase",
};

function ResponseTermsPage({ c }) {
  const naCell = { fontSize: 12, color: "#9ca3af", fontWeight: 600 };
  const valCell = { fontSize: 12.5, color: "#1a1a1a", fontWeight: 700 };
  return (
    <div className="page" style={PAGE_BASE}>
      <HeaderBar customerName={c.customerName} accent={SILVER.accentSoft} />
      <GhostNumber n="08" />
      <div style={{ padding: "40px 64px 48px", position: "relative", minHeight: PAGE_BODY_H }}>
        <SectionLabel text="Section 08" accent={SILVER.accentSoft} />
        <h2 style={{ fontSize: 26, fontWeight: 800, color: "#1a1a1a", margin: "0 0 6px",
                     letterSpacing: "-0.02em" }}>Response Times &amp; Service Terms</h2>
        <p style={{ fontSize: 13, color: "#4b5563", margin: "0 0 18px", lineHeight: 1.45 }}>
          Our commitment to fast, reliable support — and the terms that keep it fair for{" "}
          {c.customerName} and every one of our clients.
        </p>

        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12, marginBottom: 20 }}>
          <thead>
            <tr>
              <th style={{ textAlign: "left", padding: "8px 14px 8px 0", width: "14%" }}>
                <span style={{ fontSize: 10, fontWeight: 700, color: "#1a1a1a",
                               letterSpacing: "0.08em", textTransform: "uppercase" }}>Priority</span>
                <div style={{ width: 22, height: 2, background: SILVER.accent, marginTop: 4 }} />
              </th>
              <th style={{ textAlign: "left", padding: "8px 14px", width: "44%" }}>
                <span style={thMuted}>Examples</span>
              </th>
              <th style={{ textAlign: "center", padding: "8px 10px", width: "14%", background: "#f8fafc" }}>
                <span style={{ ...thMuted, fontSize: 9.5, lineHeight: 1.3 }}>Copper &amp; Bronze</span>
              </th>
              {/* Silver's own column, tinted to match the comparison table. */}
              <th style={{ textAlign: "center", padding: "8px 10px", width: "14%",
                           background: SILVER.columnTint }}>
                <span style={{ ...thMuted, fontSize: 9.5 }}>Silver</span>
              </th>
              <th style={{ textAlign: "center", padding: "8px 10px", width: "14%" }}>
                <span style={{ ...thMuted, fontSize: 9.5 }}>Gold Fortress</span>
              </th>
            </tr>
          </thead>
          <tbody>
            {PRIORITY_ROWS.map(([color, name, example, cb, silver, gold], i) => (
              <tr key={name}
                  style={{ borderTop: "1px solid #f0f0f0",
                           borderBottom: i === PRIORITY_ROWS.length - 1 ? "1px solid #f0f0f0" : undefined }}>
                <td style={{ padding: "10px 14px 10px 0", verticalAlign: "top" }}>
                  <span style={{ display: "inline-block", width: 7, height: 7, borderRadius: "50%",
                                 background: color, marginRight: 7, verticalAlign: "middle" }} />
                  <span style={{ fontSize: 12.5, fontWeight: 700, color: "#1a1a1a", verticalAlign: "middle" }}>
                    {name}
                  </span>
                </td>
                <td style={{ padding: "10px 14px", verticalAlign: "top", fontSize: 11.5,
                             color: "#4b5563", lineHeight: 1.45 }}>{example}</td>
                <td style={{ textAlign: "center", padding: 10, verticalAlign: "top",
                             background: "#f8fafc", ...naCell }}>{cb}</td>
                <td style={{ textAlign: "center", padding: 10, verticalAlign: "top",
                             background: SILVER.columnTint, ...(silver === "N/A" ? naCell : valCell) }}>
                  {silver}
                </td>
                <td style={{ textAlign: "center", padding: 10, verticalAlign: "top",
                             ...(gold === "N/A" ? naCell : valCell) }}>{gold}</td>
              </tr>
            ))}
          </tbody>
        </table>

        <div style={{ border: "1px solid #e5e7eb", borderRadius: 8, overflow: "hidden" }}>
          <SectionBand title="Basic Terms &amp; Conditions" accent={SILVER.accent}
                       extraStyle={{ padding: "8px 20px" }} />
          <div style={{ padding: "14px 20px" }}>
            {TERMS.map((term, i) => (
              <BulletRow key={i} bullet={dot(SILVER.accent)} bottom={i === TERMS.length - 1 ? 0 : 10}>
                <p style={{ fontSize: 12, color: "#374151", lineHeight: 1.55, margin: 0 }}>{term}</p>
              </BulletRow>
            ))}
          </div>
        </div>
      </div>
      <PageFooter />
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════════════
   PAGE 9 — Foundational Upgrade Plan
   The same priced-block system Bronze and Gold already carry. Silver's own
   source predates it and still shows the old static placeholder; that is a
   stale source, not a smaller feature set.
   ══════════════════════════════════════════════════════════════════════ */
const fbTh = {
  padding: "4px 0", textAlign: "left", fontSize: 9, fontWeight: 700,
  color: "#6b7280", letterSpacing: "0.08em", textTransform: "uppercase",
};

/* One FRAGMENT of a block's card. A block that fits renders as a single
   fragment and looks exactly as it always did; a long one renders as several,
   each a closed rounded card, each continuation re-announcing which block it
   belongs to and repeating the column headers. */
function FoundationalBlockCard({ block, num, items: fragItems, isFirst = true,
                                 isLast = true, continuesBelow = false }) {
  const items = Array.isArray(fragItems)
    ? fragItems
    : (Array.isArray(block.lineItems) ? block.lineItems : []);
  const label = String(num).padStart(2, "0");
  return (
    <div style={{ border: "1px solid #e5e7eb", borderRadius: 8, padding: 16,
                  position: "relative", overflow: "hidden", marginBottom: 12 }}>
      <div style={{ position: "absolute", top: 0, left: 0, right: 0, height: 3,
                    background: SILVER.accent }} />
      {isFirst ? (
        <>
      <SplitRow
        align="top"
        style={{ marginBottom: 10 }}
        left={
          <div style={{ width: 28, height: 28, background: "#1a1a1a", borderRadius: 6,
                        textAlign: "center", lineHeight: "28px" }}>
            <span style={{ color: SILVER.accentSoft, fontSize: 11, fontWeight: 800,
                           letterSpacing: "-0.02em" }}>
              {String(num).padStart(2, "0")}
            </span>
          </div>
        }
        right={
          <span style={{ fontSize: 9, fontWeight: 600, color: "#4b5563",
                         background: "#f3f4f6", padding: "2px 7px", borderRadius: 20,
                         letterSpacing: "0.04em", textTransform: "uppercase" }}>
            Quoted Separately
          </span>
        }
      />

      <h3 style={{ fontSize: 13.5, fontWeight: 700, color: "#1a1a1a", margin: "0 0 6px",
                   letterSpacing: "-0.01em" }}>{block.title}</h3>
      {block.description && (
        <p style={{ fontSize: 12, color: "#4b5563", lineHeight: 1.6, margin: "0 0 8px" }}>
          {block.description}
        </p>
      )}
      {block.timeline && (
        <p style={{ fontSize: 11, color: "#6b7280", margin: "0 0 8px", fontStyle: "italic" }}>
          {block.timeline}
        </p>
      )}
        </>
      ) : (
        /* Continuation header: same block number and title, marked as carried
           over. No description, timeline or QUOTED SEPARATELY badge — those
           belong to the block's opening, and repeating them would read as a
           second, near-identical project. */
        <div style={{ marginBottom: 8 }}>
          <table style={{ borderCollapse: "collapse" }}><tbody><tr>
            <td style={{ padding: 0, paddingRight: 10, verticalAlign: "middle" }}>
              <div style={{ width: 28, height: 28, background: "#1a1a1a", borderRadius: 6,
                            textAlign: "center", lineHeight: "28px" }}>
                <span style={{ color: SILVER.accentSoft, fontSize: 11, fontWeight: 800,
                               letterSpacing: "-0.02em" }}>{label}</span>
              </div>
            </td>
            <td style={{ padding: 0, verticalAlign: "middle" }}>
              <span style={{ fontSize: 13.5, fontWeight: 700, color: "#1a1a1a",
                             letterSpacing: "-0.01em" }}>
                {label} (cont.) — {block.title}
              </span>
            </td>
          </tr></tbody></table>
        </div>
      )}

      {items.length > 0 && (
        <table style={{ width: "100%", fontSize: 11.5, borderCollapse: "collapse", marginTop: 4 }}>
          <thead>
            <tr style={{ borderBottom: "1px solid #e5e7eb" }}>
              <th style={fbTh}>Item</th>
              <th style={{ ...fbTh, textAlign: "center", width: 44 }}>Qty</th>
              <th style={{ ...fbTh, textAlign: "right", width: 76 }}>Price</th>
              <th style={{ ...fbTh, textAlign: "right", width: 84 }}>Total</th>
            </tr>
          </thead>
          <tbody>
            {items.map((li, j) => (
              <tr key={li.id || j} style={{ borderBottom: "1px solid #f3f4f6" }}>
                <td style={{ padding: "5px 0", color: "#1f2937", verticalAlign: "top" }}>
                  <span style={{ fontWeight: 600 }}>{li.description}</span>
                  {li.manufacturer && (
                    <span style={{ color: "#9ca3af" }}> · {li.manufacturer}</span>
                  )}
                  {li.detail && (
                    <div style={{ fontSize: 10.5, color: "#6b7280", lineHeight: 1.45,
                                  marginTop: 2 }}>{li.detail}</div>
                  )}
                </td>
                <td style={{ padding: "5px 8px", textAlign: "center", color: "#4b5563",
                             verticalAlign: "top" }}>{li.quantity}</td>
                <td style={{ padding: "5px 8px", textAlign: "right", color: "#4b5563",
                             verticalAlign: "top", whiteSpace: "nowrap" }}>
                  {fmtMoney(li.unitPrice)}
                </td>
                <td style={{ padding: "5px 0", textAlign: "right", fontWeight: 600,
                             color: "#1a1a1a", verticalAlign: "top", whiteSpace: "nowrap" }}>
                  {fmtMoney(lineItemTotal(li))}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {/* The subtotal belongs to the block, so it prints once, on the fragment
          that closes it — never repeated per page and never left on its own. */}
      {isLast ? (
        <SplitRow
          style={{ marginTop: 8 }}
          left={null}
          right={
            <span style={{ fontSize: 12.5, fontWeight: 700, color: "#1a1a1a",
                           whiteSpace: "nowrap" }}>
              Subtotal (ex GST): {fmtMoney(computeBlockSubtotal(block))}
            </span>
          }
        />
      ) : continuesBelow ? (
        <p style={{ fontSize: 10, color: "#9ca3af", fontStyle: "italic",
                    margin: "6px 0 0", textAlign: "right" }}>
          continued on next page →
        </p>
      ) : null}
    </div>
  );
}

function FoundationalPage({ c }) {
  const blocks = Array.isArray(c.foundationalBlocks) ? c.foundationalBlocks : [];
  /* The note is passed in because it consumes first-page height. Packing
     without it would let a block overflow a page that still believed it had
     room, and `.page` is overflow:hidden — the block would vanish, not
     reflow. Same rule as the title: first page only. */
  const pricingNote = String(c.foundationalPricingNote || "").trim();
  const pages = chunkFoundationalBlocks(blocks, pricingNote);

  return (
    <>
      {pages.map((pageBlocks, pageIdx) => (
        <div key={pageIdx} className="page" data-flow="foundational" style={PAGE_BASE}>
          <HeaderBar customerName={c.customerName} accent={SILVER.accentSoft} />
          <GhostNumber n={pageIdx === 0 ? "07" : `07.${pageIdx + 1}`} />
          <div style={{ padding: "44px 64px 52px", position: "relative", minHeight: PAGE_BODY_H }}>
            {pageIdx === 0 ? (
              <>
                <SectionLabel text="Section 07" accent={SILVER.accentSoft} />
                <h2 style={{ fontSize: 28, fontWeight: 800, color: "#1a1a1a", margin: "0 0 6px",
                             letterSpacing: "-0.02em" }}>Foundational Upgrade Plan</h2>
                <p style={{ fontSize: 13.5, color: "#4b5563", margin: "0 0 16px", lineHeight: 1.5 }}>
                  These one-off projects address the critical gaps identified during our
                  assessment. Each is quoted separately and can be staged based on priority
                  and budget.
                </p>
                {/* Estimate-only caveat. Same small grey italic treatment as the
                    Investment page's GST line — reused, not reinvented. Suppressed
                    entirely when blank so an emptied field leaves no gap. */}
                {pricingNote && (
                  <p style={{ fontSize: 11.5, color: "#4b5563", margin: "0 0 16px",
                              fontStyle: "italic", lineHeight: 1.5 }}>
                    {pricingNote}
                  </p>
                )}
              </>
            ) : (
              <p style={{ fontSize: 11, color: "#9ca3af", fontStyle: "italic", margin: "0 0 16px" }}>
                Foundational Upgrade Plan (continued)
              </p>
            )}

            {blocks.length === 0 ? (
              <EmptyBlock title="No projects added yet"
                          hint="Open the settings panel → Foundational Upgrade → Add Block"
                          pad={40} />
            ) : (
              /* Fragments, not blocks: a long block arrives as several, each
                 already carrying its own items, header kind and closing state.
                 The number comes from the fragment so a continuation keeps its
                 block's number rather than taking the next one. */
              pageBlocks.map((frag, fi) => (
                <FoundationalBlockCard
                  key={`${frag.block.id || frag.num}-${fi}`}
                  block={frag.block} num={frag.num} items={frag.items}
                  isFirst={frag.isFirst} isLast={frag.isLast}
                  continuesBelow={frag.continuesBelow} />
              ))
            )}
          </div>
          <PageFooter />
        </div>
      ))}
    </>
  );
}

/* ══════════════════════════════════════════════════════════════════════
   PAGE 10 — Why Choose Datatech
   ══════════════════════════════════════════════════════════════════════ */
/* ⚠️ Silver's third stat is "∞ Helpdesk Support", NOT Bronze's "24/7 SOC
   Monitoring". Silver's headline benefit over Bronze IS the unlimited
   helpdesk, so the page leads with it. Verified in the source. */
const STATS = [
  ["20+",  "Years Experience"],
  ["75+",  "WA Businesses"],
  ["∞",    "Helpdesk Support"],
  ["2hr",  "SLA Response"],
];

const DIFFERENTIATORS = [
  ["Complete Technology Partner", "IT support is just the beginning. We cover phones, internet, CCTV, Wi-Fi, access control, digital signage, and meeting rooms — all under one expert provider."],
  ["Built on Trust", "We treat your business and budget as if they were our own. We own our mistakes, always strive for honest service, and hold ourselves to the highest standard of accountability."],
  ["An Extension of Your Team", "Most of our clients consider us part of their own company. We work side by side with your staff — providing expert support, proactive problem solving, and guidance aligned to your goals."],
  ["Local Perth Team", "We're based in Perth, but service customers throughout WA from the Kimberley to the Great Southern.  We understand WA businesses. We know local compliance, local connectivity challenges, and what it means to be a genuinely committed local technology partner."],
];

function WhyUsPage({ c, assetBase }) {
  return (
    <div className="page" style={{ ...PAGE_BASE, height: 1123, minHeight: 1123 }}>
      <div style={{ position: "relative", zIndex: 3 }}>
        <HeaderBar customerName={c.customerName} accent={SILVER.accentSoft} />
      </div>

      <div style={{ position: "absolute", top: -100, right: -100, width: 400, height: 400,
                    borderRadius: "50%", background: "rgba(100,116,139,0.07)", pointerEvents: "none" }} />
      <div style={{ position: "absolute", bottom: 90, left: -60, width: 280, height: 280,
                    borderRadius: "50%", background: "rgba(100,116,139,0.07)", pointerEvents: "none" }} />
      <div style={{ position: "absolute", top: 0, left: 0, right: 0, bottom: 0,
                    backgroundImage: "radial-gradient(rgba(0,0,0,0.04) 1px, transparent 1px)",
                    backgroundSize: "22px 22px", pointerEvents: "none" }} />
      <div style={{ position: "absolute", top: 0, left: 0, right: 0, height: 4, zIndex: 2,
                    background: SILVER.topBar }} />

      <div style={{ padding: "28px 64px 32px", position: "relative", zIndex: 1 }}>
        <table style={{ width: "100%", borderCollapse: "separate", borderSpacing: 1,
                        background: "#1a1a1a", borderRadius: 10, overflow: "hidden",
                        marginBottom: 40, tableLayout: "fixed" }}>
          <tbody>
            <tr>
              {STATS.map(([value, label]) => (
                <td key={label} style={{ background: "#222222", padding: "22px 20px", textAlign: "center" }}>
                  <p style={{ fontSize: 32, fontWeight: 800, color: SILVER.accentSoft, margin: 0,
                              letterSpacing: "-0.03em", lineHeight: 1 }}>{value}</p>
                  <p style={{ fontSize: 11, color: "rgba(255,255,255,0.55)", margin: "6px 0 0",
                              letterSpacing: "0.06em", textTransform: "uppercase", fontWeight: 500 }}>
                    {label}
                  </p>
                </td>
              ))}
            </tr>
          </tbody>
        </table>

        <SectionLabel text="Section 09" accent={SILVER.accentSoft} />
        <h2 style={{ fontSize: 28, fontWeight: 800, color: "#1a1a1a", margin: "0 0 6px",
                     letterSpacing: "-0.02em" }}>Why Choose Datatech?</h2>
        <p style={{ fontSize: 13.5, color: "#4b5563", margin: "0 0 28px", lineHeight: 1.5 }}>
          We're more than an IT provider — we're your complete technology partner.
        </p>

        <TwoColTable
          items={DIFFERENTIATORS}
          colGap={14}
          rowGap={14}
          style={{ marginBottom: 32 }}
          renderItem={([title, body]) => (
            <div style={{ background: "#1a1a1a", border: "1px solid rgba(255,255,255,0.08)",
                          borderLeft: `3px solid ${SILVER.accent}`, borderRadius: 8,
                          padding: "20px 22px", height: "100%" }}>
              <h3 style={{ fontSize: 14, fontWeight: 700, color: "#ffffff", margin: "0 0 8px",
                           letterSpacing: "-0.01em" }}>{title}</h3>
              <p style={{ fontSize: 12.5, color: "rgba(255,255,255,0.6)", lineHeight: 1.6, margin: 0 }}>
                {body}
              </p>
            </div>
          )}
        />
      </div>

      <div style={{ position: "absolute", bottom: 0, left: 0, right: 0, background: "#1a1a1a",
                    padding: "24px 64px", zIndex: 3 }}>
        <SplitRow
          left={
            <>
              <p style={{ fontSize: 13, color: "rgba(255,255,255,0.55)", margin: "0 0 10px" }}>
                Ready to take the next step? Get in touch with your Datatech team.
              </p>
              <table style={{ borderCollapse: "collapse" }}>
                <tbody>
                  <tr>
                    <td style={{ verticalAlign: "middle", padding: 0, paddingRight: 28, whiteSpace: "nowrap" }}>
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={SILVER.accentSoft}
                           strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
                           style={{ verticalAlign: "middle", marginRight: 8 }}>
                        <path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07A19.5 19.5 0 013.07 10.8 19.79 19.79 0 010 2.18 2 2 0 012 0h3a2 2 0 012 1.72c.127.96.361 1.903.7 2.81a2 2 0 01-.45 2.11L6.09 7.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0122 14.92z" />
                      </svg>
                      <span style={{ fontSize: 13.5, fontWeight: 600, color: "#ffffff",
                                     verticalAlign: "middle" }}>(08) 6102 2037</span>
                    </td>
                    <td style={{ verticalAlign: "middle", padding: 0, whiteSpace: "nowrap" }}>
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={SILVER.accentSoft}
                           strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
                           style={{ verticalAlign: "middle", marginRight: 8 }}>
                        <circle cx="12" cy="12" r="10" /><line x1="2" y1="12" x2="22" y2="12" />
                        <path d="M12 2a15.3 15.3 0 014 10 15.3 15.3 0 01-4 10 15.3 15.3 0 01-4-10 15.3 15.3 0 014-10z" />
                      </svg>
                      <span style={{ fontSize: 13.5, fontWeight: 600, color: "#ffffff",
                                     verticalAlign: "middle" }}>datatech.net.au</span>
                    </td>
                  </tr>
                </tbody>
              </table>
            </>
          }
          right={
            <img src={`${assetBase}/proposal-assets/logo-dark-bg.png`} alt="Datatech Solutions"
                 style={{ height: 44, objectFit: "contain", borderRadius: 6 }} />
          }
        />
      </div>
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════════════
   The template
   ══════════════════════════════════════════════════════════════════════ */
export default function SilverTemplate({ content, mode = "edit", onChange,
                                         assetBase = "", onCoverImage }) {
  const c = { ...DEFAULT_SILVER_CONTENT(), ...(content || {}) };
  const set = React.useCallback(
    (patch) => { if (onChange) onChange(patch); },
    [onChange]
  );
  const t = computeBronzeTotals(c);

  const listPages = {
    challenges: {
      key: "challenges", ghost: "02", section: "Section 02", heading: "What We Heard",
      subtitle: (name) => `Key challenges and observations shared during my discovery session with ${name}.`,
      emptyTitle: "No challenges added yet",
      emptyHint: "Open the settings panel → What We Heard → Add Challenge",
      addLabel: "Add Challenge",
      icons: CHALLENGE_ICONS, accent: SILVER.accent, iconBg: "#f1f5f9", iconStroke: SILVER.accent,
    },
    findings: {
      key: "findings", ghost: "03", section: "Section 03", heading: "What We've Found",
      subtitle: (name) => `Our technical assessment findings following our review of ${name}'s environment.`,
      emptyTitle: "No findings added yet",
      emptyHint: "Open the settings panel → What We've Found → Add Finding",
      addLabel: "Add Finding",
      icons: FINDING_ICONS, accent: "#1a1a1a", iconBg: "#f1f5f9", iconStroke: "#374151",
    },
  };

  return (
    <div className="proposal-silver doc-pages" data-mode={mode}>
      <CoverPage c={c} mode={mode} set={set} assetBase={assetBase} onCoverImage={onCoverImage}
                 accent={SILVER.accentSoft} topBar={SILVER.topBar} />
      {c.showIntro !== false         && <IntroPage c={c} mode={mode} set={set} />}
      {c.showWhatHeard !== false     && <ListCardPage c={c} mode={mode} set={set} cfg={listPages.challenges} />}
      {c.showWhatFound !== false     && <ListCardPage c={c} mode={mode} set={set} cfg={listPages.findings} />}
      {c.showRecommend !== false     && <RecommendPage c={c} t={t} />}
      {c.showComparison !== false    && <ComparisonPage c={c} />}
      {c.showInvestment !== false    && <InvestmentPage c={c} mode={mode} set={set} t={t} />}
      {c.showFoundational !== false  && <FoundationalPage c={c} />}
      {c.showResponseTerms !== false && <ResponseTermsPage c={c} />}
      {c.showWhyUs !== false         && <WhyUsPage c={c} assetBase={assetBase} />}
    </div>
  );
}
